import { useEffect, useMemo, useRef, useState } from 'react';
import { formatNumber } from '../lib/format';
import { EmptyBox } from './ErrorBox';

/**
 * Bieu do cot chong, ve tay bang SVG thuan.
 *
 * Khong dung thu vien bieu do — rang buoc cua du an, va cho mot bieu do cot chong
 * thi toan bo phep tinh chi la ti le tuyen tinh, khong dang keo them 100kB javascript.
 */

export interface StackedSeries {
  key: string;
  label: string;
  /** Mau CSS. Dung bien --viz-* de tu doi theo light/dark. */
  color: string;
}

export interface StackedPoint {
  /** Nhan day du, hien trong tooltip. */
  label: string;
  /** Nhan ngan tren truc X. */
  tick: string;
  /** Gia tri, dung thu tu cua `series`. */
  values: number[];
}

interface BarChartProps {
  series: ReadonlyArray<StackedSeries>;
  points: ReadonlyArray<StackedPoint>;
  height?: number;
  /** Ten don vi, dung trong tooltip va nhan tro giup. */
  unit?: string;
  emptyMessage?: string;
}

// --- Hang so hinh hoc. Moi so deu co ly do, khong phai chon bua. ---
/** Du cho nhan truc Y toi 6 chu so o co chu 11px. */
const PAD_LEFT = 52;
const PAD_RIGHT = 10;
const PAD_TOP = 12;
/** Mot dong nhan truc X 11px cong khoang tho. */
const PAD_BOTTOM = 26;
/** Khe giua hai cot: 2px, du de mat tach duoc hai cot lien nhau. */
const BAR_GAP = 2;
/** Khe giua hai doan chong: 2px mau nen, de hai mau khong dinh vao nhau. */
const SEGMENT_GAP = 2;
/** Bo tron dau du lieu (dinh cot) 4px — chan cot van vuong vi no tua vao truc. */
const CORNER = 4;
/** 4 duong luoi ngang: du de doc gia tri, chua den muc lam roi nen. */
const GRID_LINES = 4;
/** Nhan truc X can it nhat 64px moi khong dinh vao nhau. */
const TICK_MIN_SPACING = 64;
/** Ben duoi be rong nay thi vung ve qua hep, khong ve nua. */
const MIN_WIDTH = 280;

/** Lam tron len mot so "dep" (1/2/5 x 10^n) de nhan truc Y de doc. */
function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const exponent = Math.floor(Math.log10(value));
  const base = 10 ** exponent;
  const normalized = value / base;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * base;
}

/** Hinh chu nhat bo tron HAI goc tren — dau du lieu bo tron, chan tua truc thi vuong. */
function topRoundedPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return [
    `M ${x} ${y + h}`,
    `L ${x} ${y + rr}`,
    `Q ${x} ${y} ${x + rr} ${y}`,
    `L ${x + w - rr} ${y}`,
    `Q ${x + w} ${y} ${x + w} ${y + rr}`,
    `L ${x + w} ${y + h}`,
    'Z',
  ].join(' ');
}

function sum(values: ReadonlyArray<number>): number {
  let total = 0;
  for (const v of values) {
    if (Number.isFinite(v)) total += v;
  }
  return total;
}

export function BarChart({
  series,
  points,
  height = 220,
  unit = 'request',
  emptyMessage = 'Chưa có dữ liệu trong khoảng thời gian này',
}: BarChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(880);
  const [hovered, setHovered] = useState<number | null>(null);

  // Do be rong that de cot khong bi keo gian meo mo khi thu nho cua so.
  useEffect(() => {
    const element = containerRef.current;
    if (element === null) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;
      setWidth(Math.max(MIN_WIDTH, Math.round(entry.contentRect.width)));
    });
    observer.observe(element);
    setWidth(Math.max(MIN_WIDTH, Math.round(element.clientWidth)));
    return () => observer.disconnect();
  }, []);

  const totals = useMemo(() => points.map((p) => sum(p.values)), [points]);
  const grandTotal = useMemo(() => sum(totals), [totals]);
  const seriesTotals = useMemo(
    () =>
      series.map((_, index) =>
        sum(points.map((p) => (index < p.values.length ? (p.values[index] ?? 0) : 0))),
      ),
    [series, points],
  );

  if (points.length === 0 || grandTotal === 0) {
    return (
      <div ref={containerRef}>
        <Legend series={series} totals={seriesTotals} />
        <EmptyBox message={emptyMessage} hint="Gọi thử /createTask rồi quay lại trang này." />
      </div>
    );
  }

  const plotW = Math.max(1, width - PAD_LEFT - PAD_RIGHT);
  const plotH = Math.max(1, height - PAD_TOP - PAD_BOTTOM);
  const maxTotal = niceCeil(Math.max(...totals));
  const slot = plotW / points.length;
  const barW = Math.max(1, slot - BAR_GAP);
  const tickEvery = Math.max(
    1,
    Math.ceil(points.length / Math.max(1, Math.floor(plotW / TICK_MIN_SPACING))),
  );

  const hoveredPoint = hovered !== null ? points[hovered] : undefined;
  const hoveredTotal = hovered !== null ? (totals[hovered] ?? 0) : 0;
  const hoveredX = hovered !== null ? PAD_LEFT + slot * (hovered + 0.5) : 0;
  const hoveredTopY = PAD_TOP + plotH - (hoveredTotal / maxTotal) * plotH;
  // Tooltip dat tren dinh cot; cot cao qua thi lat xuong duoi dinh de khong tran ra ngoai.
  const tooltipBelow = hoveredTopY < 72;

  return (
    <div ref={containerRef} className="relative">
      <Legend series={series} totals={seriesTotals} />

      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Bieu do cot chong theo thoi gian, tong ${formatNumber(grandTotal)} ${unit}`}
        onMouseLeave={() => setHovered(null)}
      >
        {/* Luoi ngang + nhan truc Y */}
        {Array.from({ length: GRID_LINES + 1 }, (_, i) => {
          const ratio = i / GRID_LINES;
          const y = PAD_TOP + plotH - ratio * plotH;
          return (
            <g key={`grid-${i}`}>
              <line
                x1={PAD_LEFT}
                y1={y}
                x2={PAD_LEFT + plotW}
                y2={y}
                stroke="var(--viz-grid)"
                strokeWidth={1}
              />
              <text
                x={PAD_LEFT - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill="var(--viz-axis)"
              >
                {formatNumber(Math.round(maxTotal * ratio))}
              </text>
            </g>
          );
        })}

        {/* Cot chong */}
        {points.map((point, index) => {
          const x = PAD_LEFT + slot * index + BAR_GAP / 2;
          const baseline = PAD_TOP + plotH;
          let cursor = baseline;
          // Doan tren cung (khac 0) la doan duoc bo tron dau.
          let topMost = -1;
          for (let s = 0; s < series.length; s += 1) {
            if ((point.values[s] ?? 0) > 0) topMost = s;
          }

          const segments = series.map((serie, s) => {
            const value = point.values[s] ?? 0;
            if (value <= 0) return null;
            const rawH = (value / maxTotal) * plotH;
            const y = cursor - rawH;
            cursor = y;
            // Chua khe 2px o dinh moi doan, tru doan tren cung (dinh cot khong can khe).
            const gap = s === topMost ? 0 : SEGMENT_GAP;
            const drawH = Math.max(1, rawH - gap);
            const drawY = y + gap;
            return (
              <path
                key={serie.key}
                d={
                  s === topMost
                    ? topRoundedPath(x, drawY, barW, drawH, CORNER)
                    : topRoundedPath(x, drawY, barW, drawH, 0)
                }
                fill={serie.color}
                opacity={hovered === null || hovered === index ? 1 : 0.45}
              />
            );
          });

          return <g key={`bar-${index}`}>{segments}</g>;
        })}

        {/* Truc X */}
        <line
          x1={PAD_LEFT}
          y1={PAD_TOP + plotH}
          x2={PAD_LEFT + plotW}
          y2={PAD_TOP + plotH}
          stroke="var(--viz-axis)"
          strokeWidth={1}
        />
        {points.map((point, index) =>
          index % tickEvery === 0 ? (
            <text
              key={`tick-${index}`}
              x={PAD_LEFT + slot * (index + 0.5)}
              y={PAD_TOP + plotH + 16}
              textAnchor="middle"
              fontSize={11}
              fill="var(--viz-axis)"
            >
              {point.tick}
            </text>
          ) : null,
        )}

        {/* Vung bat chuot: phu ca chieu cao cot de de tro hon nhieu so voi ban than cot */}
        {points.map((point, index) => (
          <rect
            key={`hit-${index}`}
            x={PAD_LEFT + slot * index}
            y={PAD_TOP}
            width={slot}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => setHovered(index)}
          />
        ))}
      </svg>

      {hoveredPoint !== undefined && (
        <div
          className="pointer-events-none absolute z-10 w-52 rounded-md border border-slate-200 bg-white p-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-800"
          style={{
            left: `${Math.min(Math.max(hoveredX, 108), width - 108)}px`,
            top: `${hoveredTopY + (tooltipBelow ? 12 : -12)}px`,
            transform: tooltipBelow ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
          }}
        >
          <p className="font-semibold text-slate-900 dark:text-slate-100">{hoveredPoint.label}</p>
          <ul className="mt-1 space-y-0.5">
            {series.map((serie, s) => (
              <li key={serie.key} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ backgroundColor: serie.color }}
                  />
                  {serie.label}
                </span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {formatNumber(hoveredPoint.values[s] ?? 0)}
                </span>
              </li>
            ))}
            <li className="flex items-center justify-between gap-2 border-t border-slate-200 pt-0.5 dark:border-slate-700">
              <span className="text-slate-600 dark:text-slate-300">Tong</span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">
                {formatNumber(hoveredTotal)}
              </span>
            </li>
          </ul>
        </div>
      )}

      {/*
        Ban dang bang: bat buoc de nguoi doc bang trinh doc man hinh, hoac nguoi khong
        phan biet duoc mau, van lay duoc dung con so — mau khong bao gio la kenh duy nhat.
      */}
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
          Xem dang bang ({points.length} moc)
        </summary>
        <div className="mt-2 max-h-64 overflow-auto rounded border border-slate-200 dark:border-slate-800">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800">
              <tr>
                <th className="cf-th">Moc</th>
                {series.map((serie) => (
                  <th key={serie.key} className="cf-th text-right">
                    {serie.label}
                  </th>
                ))}
                <th className="cf-th text-right">Tong</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {points.map((point, index) => (
                <tr key={`row-${index}`}>
                  <td className="cf-td whitespace-nowrap">{point.label}</td>
                  {series.map((serie, s) => (
                    <td key={serie.key} className="cf-td text-right">
                      {formatNumber(point.values[s] ?? 0)}
                    </td>
                  ))}
                  <td className="cf-td text-right font-medium">
                    {formatNumber(totals[index] ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function Legend({
  series,
  totals,
}: {
  series: ReadonlyArray<StackedSeries>;
  totals: ReadonlyArray<number>;
}) {
  return (
    <ul className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      {series.map((serie, index) => (
        <li key={serie.key} className="flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: serie.color }}
            aria-hidden="true"
          />
          <span className="text-slate-600 dark:text-slate-300">{serie.label}</span>
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {formatNumber(totals[index] ?? 0)}
          </span>
        </li>
      ))}
    </ul>
  );
}
